/**
 * Teams Feature Types
 * Re-exports from rocks types and adds team-specific + employee directory types.
 */

export type { Team, TeamMember } from '@/features/rocks/types';

// =============================================================================
// Team Type Constants
// =============================================================================

export const TEAM_TYPES = [
  { value: 'Leadership', label: 'Leadership' },
  { value: 'Departmental', label: 'Departmental' },
  { value: 'Sales', label: 'Sales' },
  { value: 'Project', label: 'Project' },
  { value: 'Custom', label: 'Custom' },
] as const;

export type TeamType = (typeof TEAM_TYPES)[number]['value'];

export const MEMBER_ROLES = [
  { value: 'member', label: 'Member' },
  { value: 'leader', label: 'Leader' },
] as const;

export type MemberRole = (typeof MEMBER_ROLES)[number]['value'];

// =============================================================================
// Employee Directory
// =============================================================================

export type EmploymentType =
  | 'employee'
  | 'contractor'
  | 'service_account'
  | 'customer'
  | 'former';

export const EMPLOYMENT_TYPES: { value: EmploymentType; label: string; tone: string }[] = [
  { value: 'employee', label: 'Employee', tone: 'bg-emerald-100 text-emerald-800' },
  { value: 'contractor', label: 'Contractor', tone: 'bg-amber-100 text-amber-800' },
  { value: 'service_account', label: 'Service Account', tone: 'bg-slate-100 text-slate-800' },
  { value: 'customer', label: 'Customer', tone: 'bg-blue-100 text-blue-800' },
  { value: 'former', label: 'Former', tone: 'bg-zinc-100 text-zinc-600' },
];

export interface EmployeeRoleAssignment {
  role: string;
  assigned_at: string | null;
}

export interface EmployeeTeam {
  id: string;
  name: string;
  role: 'leader' | 'member';
}

export interface EmployeeManagerSummary {
  id: string;
  firstname: string | null;
  lastname: string | null;
  email: string | null;
}

export interface EmployeeReport extends EmployeeManagerSummary {
  position: string | null;
}

export interface AuditEntry {
  id: string;
  role: string;
  action: 'granted' | 'revoked';
  reason: string | null;
  actor: EmployeeManagerSummary | null;
  created_at: string;
}

export interface EmployeeRow {
  /** PG UUID — canonical user identifier in V4. */
  id: string;
  email: string | null;
  firstname: string | null;
  lastname: string | null;
  position: string | null;
  image: string | null;
  enabled: boolean;
  login_at: string | null;
  employment_type: EmploymentType | null;
  employment_start_date: string | null;
  employment_end_date: string | null;
  manager_user_id: string | null;
  employee_number: string | null;
  profile_photo_url: string | null;
  google_drive_url: string | null;
  phone: string | null;
  extension: string | null;
  mobile: string | null;
  personal_mobile: string | null;
  personal_email: string | null;
  date_of_birth: string | null;
  gender: string | null;
  social_security_number: string | null;
  permission_strings?: string[];
  roles: EmployeeRoleAssignment[];
  teams: EmployeeTeam[];
  manager: EmployeeManagerSummary | null;
}

export interface EmployeeDetail extends EmployeeRow {
  permission_strings: string[];
  reports: EmployeeReport[];
  audit: AuditEntry[];
}

export interface UpdateEmployeeInput {
  employment_type?: EmploymentType | null;
  employment_start_date?: string | null;
  employment_end_date?: string | null;
  manager_user_id?: string | null;
  position?: string | null;
  employee_number?: string | null;
  profile_photo_url?: string | null;
  google_drive_url?: string | null;
  phone?: string | null;
  extension?: string | null;
  mobile?: string | null;
  personal_mobile?: string | null;
  personal_email?: string | null;
  date_of_birth?: string | null;
  gender?: string | null;
  social_security_number?: string | null;
}

// =============================================================================
// Role Definitions
// =============================================================================

export interface RoleCapability {
  tool: string;
  label: string;
  access: 'read' | 'read-write';
}

export interface RoleDefinition {
  role: string;
  label: string;
  description: string | null;
  capabilities: RoleCapability[];
  created_at: string;
  updated_at: string;
  member_count?: number;
}

export interface UpdateRoleDefinitionInput {
  label?: string;
  description?: string;
  capabilities?: RoleCapability[];
}
